/**
 * The filter bar of the Activity pages (redesign §42.1, completing §28): one
 * row that wraps, controls M 32 with a gap of 8, each resting label saying the
 * filter (« Every member ») and an active one drawn with the ink border; a
 * discreet Clear as soon as anything narrows; on the right the count, read out
 * as it changes, then Export CSV. Applied as typed — there is no « Apply ».
 *
 * The period of a page of figures is not a filter of this kind: it is the
 * segmented control (§5) below, pushed to the right of the same row.
 */
import type React from 'react';
import { color, font, radius, shadow } from '../theme/tokens';

/** A native select as a filter (§4): M 32, its border ink once it narrows anything. */
export function filterSelect(active: boolean): React.CSSProperties {
  return {
    width: 'auto',
    height: 32,
    fontFamily: 'inherit',
    fontSize: 13,
    padding: '0 10px',
    border: `1px solid ${active ? color.ink : color.lineInput}`,
    borderRadius: radius.md,
    background: color.page,
    color: active ? color.ink : color.inkBody
  };
}

/** A date end of a period filter (§6): 124 wide in mono, ink-bordered when set. */
export function filterDate(active: boolean): React.CSSProperties {
  return { ...filterSelect(active), width: 124, padding: '0 8px', fontFamily: font.mono, fontSize: 12.5, color: color.ink };
}

/** The row itself: what narrows on the left, the count and the export pushed right. */
export function FilterBar({ children, end }: { children: React.ReactNode; end: React.ReactNode }): React.JSX.Element {
  return (
    <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8, width: '100%' }}>
      {children}
      <span style={{ display: 'flex', alignItems: 'center', gap: 8, marginLeft: 'auto' }}>{end}</span>
    </div>
  );
}

/** A discreet button (§2): no frame, ink-body text, M 32. */
export function QuietButton({
  children,
  onClick,
  href
}: {
  children: React.ReactNode;
  onClick?: () => void;
  href?: string;
}): React.JSX.Element {
  const style: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    height: 32,
    padding: '0 10px',
    border: 0,
    borderRadius: radius.md,
    background: 'transparent',
    color: color.inkBody,
    fontFamily: 'inherit',
    fontSize: 13,
    fontWeight: 500,
    textDecoration: 'none',
    cursor: 'pointer'
  };
  return href ? (
    <a href={href} onClick={onClick} className="al-quiet" style={style}>
      {children}
    </a>
  ) : (
    <button type="button" onClick={onClick} className="al-quiet" style={style}>
      {children}
    </button>
  );
}

/**
 * The count beside the export, in `aria-live` so a filter's effect is heard:
 * « Showing 50 of 150 » while nothing narrows, « 4 runs match » once something does.
 */
export function FilterCount({
  shown,
  total,
  narrowed,
  noun
}: {
  shown: number;
  total: number | null;
  narrowed: boolean;
  noun: [string, string];
}): React.JSX.Element {
  const text =
    total === null ? '' : narrowed ? `${total} ${total === 1 ? `${noun[0]} matches` : `${noun[1]} match`}` : `Showing ${shown} of ${total}`;
  return (
    <span aria-live="polite" style={{ fontSize: 12.5, color: color.inkMuted, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
      {text}
    </span>
  );
}

/**
 * A segmented control (§5): a #F5F5F7 track ringed 1 px, radius 999, padding 3;
 * the chosen segment is the white thumb with the tab shadow, its text ink 500.
 * A radiogroup: ← → move and choose. `full` is the L size at the width of its
 * column (36 high, segments sharing it), as sign-up asks it (§37.4).
 */
export function Segmented<T extends string>({
  label,
  value,
  options,
  onChange,
  full
}: {
  label: string;
  value: T;
  options: { id: T; label: string }[];
  onChange: (id: T) => void;
  full?: boolean;
}): React.JSX.Element {
  const step = (event: React.KeyboardEvent<HTMLDivElement>): void => {
    const delta = event.key === 'ArrowRight' || event.key === 'ArrowDown' ? 1 : event.key === 'ArrowLeft' || event.key === 'ArrowUp' ? -1 : 0;
    if (!delta) return;
    event.preventDefault();
    const at = options.findIndex((one) => one.id === value);
    const next = options[(at + delta + options.length) % options.length];
    onChange(next.id);
    const buttons = event.currentTarget.querySelectorAll<HTMLButtonElement>('[role="radio"]');
    buttons[options.indexOf(next)]?.focus();
  };
  return (
    <div
      role="radiogroup"
      aria-label={label}
      onKeyDown={step}
      style={{
        display: full ? 'flex' : 'inline-flex',
        width: full ? '100%' : undefined,
        boxSizing: 'border-box',
        gap: 2,
        padding: 3,
        border: `1px solid ${color.line}`,
        borderRadius: radius.chip,
        background: color.surfaceAlt
      }}
    >
      {options.map((one) => {
        const on = one.id === value;
        return (
          <button
            key={one.id}
            type="button"
            role="radio"
            aria-checked={on}
            tabIndex={on ? 0 : -1}
            onClick={() => onChange(one.id)}
            style={{
              flex: full ? 1 : undefined,
              height: full ? 28 : 26,
              padding: '0 12px',
              border: 0,
              borderRadius: radius.chip,
              background: on ? color.surface : 'transparent',
              boxShadow: on ? shadow.tab : 'none',
              color: on ? color.ink : color.inkMuted,
              fontFamily: 'inherit',
              fontSize: 13.5,
              fontWeight: on ? 500 : 400,
              cursor: 'pointer',
              transition: 'background-color 150ms ease, box-shadow 150ms ease, color 150ms ease'
            }}
          >
            {one.label}
          </button>
        );
      })}
    </div>
  );
}
